import { Box, Flex, Text } from '@chakra-ui/react'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  ChartOptions
} from 'chart.js'
import { useRef, useState, useCallback } from 'react'
import { Line } from 'react-chartjs-2'

import { LoadingBlock } from '../../../../components/Loading'
import { SectionCard } from '../../../../components/SectionCard'
import { formatCompactCurrency, formatCurrencyValue } from '../../../../helpers'
import { IMonthlyData } from '../../../../interfaces/IFinancialIndicators'
import { magenta } from '../../../../theme'

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler, Tooltip)

interface IPerformanceChartProps {
  monthlyData: IMonthlyData[]
  previousYearData: IMonthlyData[]
  loading?: boolean
}

interface ITooltipState {
  visible: boolean
  x: number
  y: number
  monthLabel: string
  year: number
  prevMonthLabel?: string
  prevYear?: number
  value: number
  prevValue?: number
  pct?: number
  datasetLabel: string
  color: string
}

const MONTHS_SHORT = ['JAN', 'FEV', 'MAR', 'ABR', 'MAI', 'JUN', 'JUL', 'AGO', 'SET', 'OUT', 'NOV', 'DEZ']
const MONTHS_LONG = [
  'JANEIRO',
  'FEVEREIRO',
  'MARÇO',
  'ABRIL',
  'MAIO',
  'JUNHO',
  'JULHO',
  'AGOSTO',
  'SETEMBRO',
  'OUTUBRO',
  'NOVEMBRO',
  'DEZEMBRO'
]

const DATASETS = [
  { label: 'Faturamento', color: magenta[500], key: 'revenue' as const },
  { label: 'Despesas', color: '#5470DE', key: 'expenses' as const },
  { label: 'Lucro', color: '#07BEAA', key: 'profit' as const }
]

// -------------------------------------------------------------------
// CustomTooltip — balão visual fiel à imagem 2
// -------------------------------------------------------------------
function CustomTooltip({ tip }: { tip: ITooltipState }) {
  if (!tip.visible) return null

  const isPositive = tip.pct !== undefined ? tip.pct >= 0 : undefined
  const pctText = tip.pct !== undefined ? `${isPositive ? '+' : ''}${tip.pct.toFixed(2)}%` : undefined
  const hasComparisonData =
    tip.prevMonthLabel !== undefined && tip.prevYear !== undefined && tip.prevValue !== undefined

  // Tamanho estimado do balão para centralizar
  const BALLOON_W = 260
  const BALLOON_H = hasComparisonData ? 140 : 100
  const ARROW_H = 10

  return (
    <Box
      position="fixed"
      pointerEvents="none"
      zIndex={99999}
      style={{
        left: tip.x - BALLOON_W / 2,
        top: tip.y - BALLOON_H - ARROW_H - 8,
        width: BALLOON_W
      }}
    >
      {/* Balão */}
      <Box
        bg="white"
        borderRadius="16px"
        boxShadow="0 8px 32px rgba(0,0,0,0.13), 0 1.5px 6px rgba(0,0,0,0.07)"
        p="16px 18px 14px"
        position="relative"
      >
        {/* Header: ícone + label + pct */}
        <Flex align="center" justify="space-between" mb="8px">
          <Flex align="center" gap="7px">
            <Box
              w="11px"
              h="11px"
              borderRadius="full"
              border="2.5px solid"
              borderColor={tip.color}
              bg="white"
              flexShrink={0}
            />
            <Text fontSize="13px" fontWeight="600" color="gray.700" lineHeight="1">
              {tip.datasetLabel}
            </Text>
          </Flex>
          {pctText && (
            <Flex align="center" gap="4px">
              <Text fontSize="13px" fontWeight="700" color={isPositive ? '#16a34a' : '#dc2626'} lineHeight="1">
                {pctText}
              </Text>
              <Text fontSize="13px" color={isPositive ? '#16a34a' : '#dc2626'} lineHeight="1">
                {isPositive ? '↑' : '↓'}
              </Text>
            </Flex>
          )}
        </Flex>

        {/* Mês/ano atual */}
        <Text fontSize="11px" fontWeight="700" color={tip.color} letterSpacing="0.06em" lineHeight="1" mb="3px">
          {tip.monthLabel}, {tip.year}
        </Text>

        {/* Valor atual — grande */}
        <Text fontSize="26px" fontWeight="800" color="gray.900" lineHeight="1.1" mb={hasComparisonData ? '10px' : '0'}>
          {formatCurrencyValue(tip.value)}
        </Text>

        {/* Seção de comparação (apenas se houver dados anteriores) */}
        {hasComparisonData && (
          <>
            {/* Divider */}
            <Box borderTop="1px solid" borderColor="gray.100" mb="8px" />

            {/* Mês/ano anterior */}
            <Text fontSize="11px" fontWeight="600" color="gray.400" letterSpacing="0.06em" lineHeight="1" mb="3px">
              {tip.prevMonthLabel}, {tip.prevYear}
            </Text>

            {/* Valor anterior — menor, cinza */}
            <Text fontSize="19px" fontWeight="700" color="gray.400" lineHeight="1">
              {tip.prevValue && formatCurrencyValue(tip.prevValue)}
            </Text>
          </>
        )}
      </Box>

      {/* Seta apontando para baixo */}
      <Box
        mx="auto"
        style={{
          width: 0,
          height: 0,
          borderLeft: '10px solid transparent',
          borderRight: '10px solid transparent',
          borderTop: '10px solid white',
          filter: 'drop-shadow(0 3px 3px rgba(0,0,0,0.07))'
        }}
      />
    </Box>
  )
}

// -------------------------------------------------------------------
// gradientPlugin — calcula o gradiente individualmente para cada dataset
// usando os valores mínimo e máximo de CADA linha, convertidos em pixels
// via a escala Y do chart.
// -------------------------------------------------------------------
const gradientPlugin = {
  id: 'gradientFill',
  afterLayout(chart: ChartJS<'line'>) {
    const { ctx, chartArea } = chart
    if (!chartArea) return

    const colors = [magenta[500], '#5470DE', '#07BEAA']
    const yScale = chart.scales['y']

    chart.data.datasets.forEach((dataset, dsIndex) => {
      const values = dataset.data as number[]
      if (!values || values.length === 0) return

      // Valor máximo e mínimo dos dados desta linha
      const maxValue = Math.max(...values)
      const minValue = Math.min(...values)

      // Converte para coordenadas de pixel na tela (Y cresce para baixo)
      // getPixelForValue retorna a posição em pixels relativa ao canvas
      const topPx = Math.max(yScale.getPixelForValue(maxValue), chartArea.top)
      const bottomPx = Math.min(yScale.getPixelForValue(minValue), chartArea.bottom)

      // Cria o gradiente apenas entre o pico e o vale desta linha
      const gradient = ctx.createLinearGradient(0, topPx, 0, bottomPx)
      gradient.addColorStop(0, `${colors[dsIndex]}55`) // topo: semi-opaco
      gradient.addColorStop(1, `${colors[dsIndex]}00`) // base: transparente

      dataset.backgroundColor = gradient
    })
  }
}

export default function PerformanceChart({ monthlyData, previousYearData, loading }: IPerformanceChartProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [tooltip, setTooltip] = useState<ITooltipState>({ visible: false } as ITooltipState)

  // Constrói o tooltip customizado a partir dos dados do chart.js
  const buildTooltip = useCallback(
    (context: { chart: ChartJS; tooltip: import('chart.js').TooltipModel<'line'> }) => {
      const { chart, tooltip: model } = context

      if (!model.opacity || model.dataPoints.length === 0) {
        setTooltip((t) => ({ ...t, visible: false }))
        return
      }

      // Usa o primeiro dataPoint (o que está sendo hovado)
      const dp = model.dataPoints[0]
      const dsIndex = dp.datasetIndex
      const dataIndex = dp.dataIndex

      const ds = DATASETS[dsIndex]
      const current = monthlyData[dataIndex]
      const prev = previousYearData.find((p) => p.month === current.month && p.year === current.year - 1)

      const value = current[ds.key] as number
      const prevValue = prev ? (prev[ds.key] as number) : undefined
      const pct = prev && prevValue !== 0 ? ((value - (prevValue ?? 0)) / Math.abs(prevValue ?? 0)) * 100 : undefined

      // Posição em coordenadas da viewport (para position: fixed)
      const canvasRect = chart.canvas.getBoundingClientRect()

      // model.caretX / model.caretY são relativas ao canvas
      // Adiciona as coordenadas do canvas para obter posição na viewport
      const x = canvasRect.left + model.caretX
      const y = canvasRect.top + model.caretY

      setTooltip({
        visible: true,
        x,
        y,
        monthLabel: MONTHS_LONG[current.month - 1],
        year: current.year,
        prevMonthLabel: prev ? MONTHS_LONG[prev.month - 1] : undefined,
        prevYear: prev ? prev.year : undefined,
        value,
        prevValue,
        pct,
        datasetLabel: ds.label,
        color: ds.color
      })
    },
    [monthlyData, previousYearData]
  )

  if (loading) {
    return (
      <SectionCard>
        <LoadingBlock h="350px" />
      </SectionCard>
    )
  }

  if (!monthlyData || monthlyData.length === 0) {
    return <Box h="350px" />
  }

  const hasMultipleYears = new Set(monthlyData.map((d) => d.year)).size > 1
  const labels = monthlyData.map((d) => {
    const monthLabel = MONTHS_SHORT[d.month - 1]
    if (!hasMultipleYears) {
      return monthLabel
    }
    return `${monthLabel}/${d.year}`
  })

  const revenueData = monthlyData.map((d) => d.revenue)
  const expensesData = monthlyData.map((d) => d.expenses)
  const profitData = monthlyData.map((d) => d.profit)

  const chartData = {
    labels,
    datasets: [
      {
        label: 'Faturamento',
        data: revenueData,
        borderColor: magenta[500],
        backgroundColor: 'transparent', // substituído pelo plugin
        borderWidth: 1,
        fill: true,
        tension: 0.4,
        pointRadius: 4,
        pointBackgroundColor: '#fff',
        pointBorderColor: magenta[500],
        pointBorderWidth: 1.5,
        pointHitRadius: 16
      },
      {
        label: 'Despesas',
        data: expensesData,
        borderColor: '#5470DE',
        backgroundColor: 'transparent', // substituído pelo plugin
        borderWidth: 1,
        fill: true,
        tension: 0.4,
        pointRadius: 4,
        pointBackgroundColor: '#fff',
        pointBorderColor: '#5470DE',
        pointBorderWidth: 1.5,
        pointHitRadius: 16
      },
      {
        label: 'Lucro',
        data: profitData,
        borderColor: '#07BEAA',
        backgroundColor: 'transparent', // substituído pelo plugin
        borderWidth: 1,
        fill: true,
        tension: 0.4,
        pointRadius: 4,
        pointBackgroundColor: '#fff',
        pointBorderColor: '#07BEAA',
        pointBorderWidth: 1.5,
        pointHitRadius: 16
      }
    ]
  }

  const options: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'nearest' as const, // ← apenas o ponto mais próximo (não todos do index)
      intersect: true // ← só dispara ao tocar o ponto
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: false, // desliga o tooltip nativo do chart.js
        external: buildTooltip // usa o nosso customizado
      }
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { color: '#999', font: { size: 11 } }
      },
      y: {
        grid: { color: '#f0f0f0' },
        ticks: {
          color: '#999',
          font: { size: 11 },
          callback: (tickValue: string | number): string | number | null => formatCompactCurrency(tickValue as number)
        }
      }
    }
  }

  // Plugin pill (mantido do original, descomentado se quiser)
  const drawRoundRect = (ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) => {
    ctx.beginPath()
    ctx.moveTo(x + r, y)
    ctx.lineTo(x + w - r, y)
    ctx.quadraticCurveTo(x + w, y, x + w, y + r)
    ctx.lineTo(x + w, y + h - r)
    ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h)
    ctx.lineTo(x + r, y + h)
    ctx.quadraticCurveTo(x, y + h, x, y + h - r)
    ctx.lineTo(x, y + r)
    ctx.quadraticCurveTo(x, y, x + r, y)
    ctx.closePath()
  }

  const pillPlugin = {
    id: 'pillLabels',
    afterDatasetsDraw: (chart: ChartJS<'line'>) => {
      const ctx = chart.ctx
      const { chartArea } = chart
      if (!chartArea) return

      // Armazena as pills desenhadas (bounding boxes) para detecção de colisão
      const drawnPills: Array<{ left: number; right: number; top: number; bottom: number }> = []

      // Cores das pills (correspondendo aos datasets)
      const pillColors = [magenta[500], '#5470DE', '#07BEAA']
      const dataArrays = [revenueData, expensesData, profitData]

      const makeBBox = (l: number, t: number, w: number, h: number) => ({
        left: l,
        right: l + w,
        top: t,
        bottom: t + h
      })

      const hasCollision = (
        a: { left: number; right: number; top: number; bottom: number },
        b: { left: number; right: number; top: number; bottom: number }
      ) => !(a.right < b.left || a.left > b.right || a.bottom < b.top || a.top > b.bottom)

      // Itera sobre os 3 datasets
      for (let dsIndex = 0; dsIndex < 3; dsIndex++) {
        const meta = chart.getDatasetMeta(dsIndex)
        const data = dataArrays[dsIndex]
        const pillColor = pillColors[dsIndex]

        meta.data.forEach((point: unknown, index: number) => {
          const value = data[index]
          if (value === 0) return

          const text = formatCompactCurrency(value)
          const pointRecord = point as Record<string, number>
          const pointX = pointRecord.x
          const pointY = pointRecord.y

          ctx.save()
          ctx.font = 'bold 10px sans-serif'
          const textWidth = ctx.measureText(text).width
          const padding = 6
          const pillWidth = textWidth + padding * 2
          const pillHeight = 16

          // Posição padrão: acima do ponto
          let pillLeft = pointX - pillWidth / 2
          let pillTop = pointY - 18 - pillHeight

          let pillBBox = makeBBox(pillLeft, pillTop, pillWidth, pillHeight)

          // Se colidir com pill anterior, tenta posicionar abaixo
          if (drawnPills.some((p) => hasCollision(pillBBox, p))) {
            pillTop = pointY + 18
            pillBBox = makeBBox(pillLeft, pillTop, pillWidth, pillHeight)
          }

          // Clampar vertical: não sair para cima nem para baixo do chartArea
          pillTop = Math.max(chartArea.top, Math.min(pillTop, chartArea.bottom - pillHeight))

          // Clampar horizontal: não sair pelas laterais do chartArea
          pillLeft = Math.max(chartArea.left, Math.min(pillLeft, chartArea.right - pillWidth))

          // Reconstrói o bbox final (após clampagem) para registrar corretamente
          pillBBox = makeBBox(pillLeft, pillTop, pillWidth, pillHeight)
          drawnPills.push(pillBBox)

          // Desenha a pill
          ctx.fillStyle = pillColor
          drawRoundRect(ctx, pillLeft, pillTop, pillWidth, pillHeight, 8)
          ctx.fill()

          // Desenha o texto centrado dentro da pill (usa pillLeft+pillWidth/2 pois
          // pillLeft pode ter sido deslocado pelo clampar horizontal)
          ctx.fillStyle = '#FFF'
          ctx.textAlign = 'center'
          ctx.textBaseline = 'middle'
          ctx.fillText(text, pillLeft + pillWidth / 2, pillTop + pillHeight / 2)
          ctx.restore()
        })
      }
    }
  }

  return (
    <SectionCard
      title="Performance (R$)"
      titleAs="h3"
      actions={
        <Flex gap={4} align="center">
          {DATASETS.map((ds) => (
            <Flex key={ds.label} align="center" gap={1}>
              <Box w="10px" h="10px" borderRadius="full" bg={ds.color} />
              <Text variant="caption">{ds.label}</Text>
            </Flex>
          ))}
        </Flex>
      }
    >
      {/* Container relativo — o tooltip se posiciona aqui dentro */}
      <Box h="350px" position="relative" ref={containerRef}>
        <Line key={labels.toString()} data={chartData} options={options} plugins={[gradientPlugin, pillPlugin]} />
        <CustomTooltip tip={tooltip} />
      </Box>
    </SectionCard>
  )
}
